import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import services from "../../services/services";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { useAppDispatch } from "../../store/app/store";
import { getallUsersThank } from "../../store/features/getUsersSlice";
import { useSelector } from "react-redux";
import { getTourName } from "../../services/Tour";
import { utils } from "../../utils/util";

// ---------------------------------------------------------------------------
// Language provenance helpers (2026-09 fix plan, WS5-2). Everything shown here
// comes from recorded facts: the app's own language report, the booking the
// code was actually used on, and the e-mail record book. Nothing is guessed
// by matching a tour name; "Unknown" and "Not verified" are written out.
// ---------------------------------------------------------------------------
const LANG_NAMES: Record<string, string> = {
  en: "English", fr: "French", de: "German", es: "Spanish", it: "Italian", zh: "Chinese",
  hi: "Hindi", ru: "Russian", nl: "Dutch", pt: "Portuguese", pl: "Polish", cs: "Czech",
  sv: "Swedish", ta: "Tamil", ja: "Japanese", ko: "Korean", da: "Danish", el: "Greek", gu: "Gujarati",
  english: "English", french: "French", german: "German", spanish: "Spanish", italian: "Italian", chinese: "Chinese",
};
const langName = (code: string | null | undefined): string | null =>
  code ? LANG_NAMES[String(code).toLowerCase()] || String(code) : null;

const SOURCE_LABELS: Record<string, string> = {
  explicit_selection: "chosen by the user",
  device_inferred: "inferred from the device",
  legacy_unverified: "migrated from the old app, not verified",
};
const sourceLabel = (source: string | null | undefined) => (source ? SOURCE_LABELS[source] || source : null);

const fmtDate = (value: any) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
const fmtDateTime = (value: any) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const Pill = ({ tone, children, title }: { tone: "green" | "gray" | "amber" | "red" | "blue" | "purple" | "orange"; children: any; title?: string }) => {
  const cls: Record<string, string> = {
    green: "bg-green-100 text-green-700",
    gray: "bg-gray-100 text-gray-600",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-red-100 text-red-700",
    blue: "bg-blue-100 text-blue-700",
    purple: "bg-purple-100 text-purple-700",
    orange: "bg-orange-100 text-orange-700",
  };
  return <span title={title} className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${cls[tone]}`}>{children}</span>;
};

const LanguageStatusPill = ({ status }: { status: string | null | undefined }) => {
  switch (status) {
    case "known": return <Pill tone="green">Known</Pill>;
    case "conflict": return <Pill tone="amber" title="The booking carries two different languages">Conflict</Pill>;
    case "unknown": return <Pill tone="gray" title="No language evidence on this booking">Unknown</Pill>;
    default: return <Pill tone="gray" title="Imported before language checking existed (7 Sep 2026)">Not verified</Pill>;
  }
};

const EmailStatusPill = ({ email }: { email: any }) => {
  if (!email) return <Pill tone="gray" title="Sent before the e-mail record book started (7 Sep 2026)">Not recorded</Pill>;
  switch (email.status) {
    case "accepted": return <Pill tone="green" title="Accepted by the mail server (not proof of delivery)">Accepted</Pill>;
    case "failed": return <Pill tone="red">Failed</Pill>;
    case "unknown_outcome": return <Pill tone="amber" title="The mail server never answered">Unknown outcome</Pill>;
    case "pending": return <Pill tone="blue">Sending…</Pill>;
    default: return <Pill tone="gray">{email.status}</Pill>;
  }
};

const PlatformPill = ({ platform }: { platform: string | null | undefined }) => {
  if (!platform) return null;
  const tone = platform === "GetYourGuide" ? "orange" : platform === "Viator" ? "green" : platform === "Website" ? "blue" : "gray";
  return <Pill tone={tone as any}>{platform}</Pill>;
};

const ORIGIN_LABELS: Record<string, string> = { iap: "App purchase", redeem: "Tour code", manual_grant: "Manual grant" };

const UserProfile = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const dispatch: any = useAppDispatch();
  const { Tours } = useSelector((state: any) => state.tours);
  const { adminPermission } = useSelector((state: any) => state.counter);

  const [profile, setProfile] = useState<any>(null);
  const [userTours, setUserTours] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddTour, setShowAddTour] = useState(false);
  const [tourSearch, setTourSearch] = useState("");
  const [selectedTour, setSelectedTour] = useState<any>(null);

  const userTourIds = userTours?.map((t: any) => t.tourId);
  const filteredTours = Tours?.data?.data
    ?.filter((tour: any) => !userTourIds?.includes(tour.productId))
    .map((tour: any) => ({
      ...tour,
      name: getTourName(tour?.name, "en") || "",
    })) || [];

  const formik = useFormik({
    initialValues: {
      firstName: "",
      email: "",
      contact: "",
      id: userId,
    },
    validate: (values: any) => {
      const errors: any = {};
      if (!values?.firstName?.trim()) errors.firstName = "Name is required";
      if (!values?.email?.trim()) errors.email = "Email is required";
      else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email.trim()))
        errors.email = "Invalid email";
      return errors;
    },
    onSubmit: async (values) => {
      const res = await services.editUser({
        ...values,
        email: values.email?.trim(),
        contact: values.contact?.trim(),
      });
      if ((res as any)?.status === 200) {
        toast.success("User updated");
        setIsEditing(false);
        loadProfile();
      } else {
        toast.error("Update failed");
      }
    },
    enableReinitialize: true,
  });


  const loadProfile = async () => {
    try {
      setLoading(true);
      const res = await services.getUserProfile(userId!);
      setProfile(res);
      formik.setValues({
        firstName: res.user.firstName || "",
        email: res.user.email || "",
        contact: res.user.contact || "",
        id: userId!,
      });
    } catch (err) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const loadUserTours = async () => {
    try {
      const response: any = await services.userTourList(userId);
      const allTours: any = await services.getAllTour();
      const allBundles: any = await services.getActiveBundles();
      for (const item of response?.data || []) {
        if (!item.destinationName) {
          for (const tour of allTours?.data?.data || []) {
            if (item.tourId === tour.productId) {
              item.destinationName = utils.getText(tour.name);
              break;
            }
          }
          if (!item.destinationName) {
            const bundle = allBundles?.data?.data?.find((b: any) => b?.productId === item.tourId);
            if (bundle) item.destinationName = `Bundle - ${bundle.name}`;
          }
        }
      }
      setUserTours(response?.data || []);
    } catch { }
  };

  const handleDelete = async () => {
    try {
      await services.deleteUserApi(userId);
      toast.success("User deleted");
      dispatch(getallUsersThank({ page: 1, pageSize: 15 }));
      navigate("/users");
    } catch {
      toast.error("Error deleting user");
    }
  };

  useEffect(() => {
    if (userId) {
      loadProfile();
      loadUserTours();
    }
  }, [userId]);

  if (loading) {
    return (
      <div className="flex w-full h-[80vh] justify-center items-center">
        <h1 className="text-aqua text-2xl">Loading profile...</h1>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex w-full h-[80vh] justify-center items-center">
        <h1 className="text-red-500 text-2xl">User not found</h1>
      </div>
    );
  }

  const { user, stats } = profile;
  // v2 keys from the server (additive): exact code→account links and the
  // purchases that predate them. Older servers simply return neither.
  const linked: any[] = Array.isArray(profile.redemptionsV2) ? profile.redemptionsV2 : [];
  const legacy: any[] = Array.isArray(profile.legacyEntitlements) ? profile.legacyEntitlements : [];
  const verifiedLanguages: string[] = Array.from(new Set([
    ...(user.appLanguage ? [`App: ${langName(user.appLanguage)}`] : []),
    ...linked
      .filter((r: any) => r.language?.status === "known" && r.language?.comm)
      .map((r: any) => `Booking: ${langName(r.language.comm)}`),
  ]));
  // Legacy rows the server could tie to a booking with reasonable confidence
  // (same e-mail/phone on the booking, or the tour has only one booking).
  const guesses: any[] = legacy.filter((r: any) => r.bestGuess && (r.bestGuess.match === "email_and_tour" || r.bestGuess.match === "only_booking_for_tour"));
  const guessLabel = (m: string) => (m === "email_and_tour" ? "e-mail matches this booking" : "only booking for this tour");
  const guessedLanguages: string[] = Array.from(new Set(
    guesses.map((r: any) => langName(r.bestGuess.commLanguage) || langName(r.bestGuess.bookingLanguage)).filter(Boolean) as string[]
  ));

  return (
    <div className="px-8 py-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-aqua hover:underline font-semibold"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Users
        </button>
        <div className="flex gap-3">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="px-4 py-2 border border-aqua text-aqua rounded-lg hover:bg-aqua hover:text-white transition"
          >
            {isEditing ? "Cancel Edit" : "Edit"}
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Top section: User Info + Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* User Info Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">User Info</h2>
          {isEditing ? (
            <form onSubmit={formik.handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-gray-500">Name</label>
                <input
                  name="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                />
                {formik.errors.firstName && <p className="text-red-500 text-xs mt-1">{formik.errors.firstName as string}</p>}
              </div>
              <div>
                <label className="text-sm text-gray-500">Email</label>
                <input
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                />
                {formik.errors.email && <p className="text-red-500 text-xs mt-1">{formik.errors.email as string}</p>}
              </div>
              <div>
                <label className="text-sm text-gray-500">Phone</label>
                <input
                  name="contact"
                  value={formik.values.contact}
                  onChange={formik.handleChange}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                />
              </div>
              <button type="submit" className="px-6 py-2 bg-aqua text-white rounded-lg">
                Save
              </button>
            </form>
          ) : (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Name</span>
                <span className="font-medium">{user.firstName} {user.lastName || ""}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">App Email</span>
                <span className="font-medium">{user.email || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Phone</span>
                <span className="font-medium">{user.contact || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Joined</span>
                <span className="font-medium">
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "—"}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Stats Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Stats</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Entitlements</span>
              <span className="text-right">
                <span className="font-bold text-aqua text-xl">{linked.length + legacy.length || stats.totalRedemptions}</span>
                <span className="block text-xs text-gray-500">{linked.length} linked · {legacy.length} legacy</span>
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Cities</span>
              <div className="flex flex-wrap gap-1 justify-end">
                {stats.cities.length > 0 ? stats.cities.map((city: string) => (
                  <span key={city} className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">{city}</span>
                )) : <span className="text-gray-400">—</span>}
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Platforms</span>
              <div className="flex flex-wrap gap-1 justify-end">
                {stats.platforms.length > 0 ? stats.platforms.map((p: string) => (
                  <span key={p} className={`text-xs px-2 py-1 rounded-full ${
                    p === 'GetYourGuide' ? 'bg-orange-100 text-orange-700' :
                    p === 'Viator' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>{p}</span>
                )) : <span className="text-gray-400">—</span>}
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Languages (verified)</span>
              <div className="flex flex-wrap gap-1 justify-end">
                {verifiedLanguages.length > 0 ? verifiedLanguages.map((l: string) => (
                  <span key={l} className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full">{l}</span>
                )) : guessedLanguages.length > 0 ? guessedLanguages.map((l: string) => (
                  <span key={l} className="bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full" title="Best guess from the booking that carries this account's e-mail">{l} (guess)</span>
                )) : <Pill tone="gray" title="No language has been confirmed by the app or a linked booking yet">Not verified</Pill>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Languages — four recorded facts, never a guess */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-800 mb-1">Languages</h2>
        <p className="text-xs text-gray-400 mb-4">
          Recorded facts only. "Not verified" means the data predates the checks that started on 7 Sep 2026.
        </p>
        <div className="space-y-4">
          {/* 1. App language */}
          <div className="flex justify-between gap-6">
            <span className="text-gray-500 shrink-0">App language</span>
            <div className="text-right">
              {user.appLanguage ? (
                <>
                  <span className="font-medium">{langName(user.appLanguage)}</span>
                  <span className="block text-xs text-gray-500">
                    {sourceLabel(user.appLanguageSource)}
                    {user.appLanguageChangedAt ? ` · ${fmtDateTime(user.appLanguageChangedAt)}` : ""}
                    {user.appLanguagePlatform ? ` · ${user.appLanguagePlatform}${user.appLanguageAppVersion ? ` ${user.appLanguageAppVersion}` : ""}` : ""}
                  </span>
                </>
              ) : (
                <>
                  <Pill tone="gray">Unknown</Pill>
                  <span className="block text-xs text-gray-500 mt-1">No report from the app yet (needs iOS 2.0.1 / Android 1.9.17 or later)</span>
                </>
              )}
              {user.legacyEmailLanguage && (
                <span className="block text-xs text-gray-500 mt-1">Manual-grant e-mail language (old field): <span className="capitalize">{user.legacyEmailLanguage}</span></span>
              )}
            </div>
          </div>

          {/* 2. Booking communication language */}
          <div className="flex justify-between gap-6">
            <span className="text-gray-500 shrink-0">Booking communication language</span>
            <div className="text-right space-y-1">
              {linked.length > 0 ? linked.map((r: any) => (
                <div key={`c-${r.redemptionId}`} className="text-sm">
                  <span className="text-gray-500 text-xs">{r.tourName || r.productId}: </span>
                  <span className="font-medium">{langName(r.language?.comm) || "Unknown"}</span>{" "}
                  <LanguageStatusPill status={r.language?.status} />
                  {r.language?.evidence && (
                    <span className="block text-[11px] text-gray-400 font-mono truncate max-w-[420px]" title={r.language.evidence}>{r.language.evidence}</span>
                  )}
                </div>
              )) : guesses.length > 0 ? guesses.map((r: any) => (
                <div key={`cg-${r.paymentId}`} className="text-sm">
                  <span className="text-gray-500 text-xs">{r.tourName || r.productId}: </span>
                  <span className="font-medium">{langName(r.bestGuess.commLanguage) || langName(r.bestGuess.bookingLanguage) || "Unknown"}</span>{" "}
                  <Pill tone="amber" title={`Best guess: ${guessLabel(r.bestGuess.match)}`}>Best guess</Pill>
                  <span className="block text-[11px] text-gray-400">{guessLabel(r.bestGuess.match)} · from the old booking language field</span>
                </div>
              )) : (
                <>
                  <Pill tone="gray">Not verified</Pill>
                  <span className="block text-xs text-gray-500 mt-1">
                    {legacy.length > 0
                      ? "These entitlements predate the code-to-account link and no booking for these tours carries this account's e-mail."
                      : "No entitlements yet."}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* 3. Booking audio language */}
          <div className="flex justify-between gap-6">
            <span className="text-gray-500 shrink-0">Booking audio language</span>
            <div className="text-right space-y-1">
              {linked.length > 0 ? linked.map((r: any) => (
                <div key={`a-${r.redemptionId}`} className="text-sm">
                  <span className="text-gray-500 text-xs">{r.tourName || r.productId}: </span>
                  <span className="font-medium">{langName(r.language?.audio) || "Unknown"}</span>
                </div>
              )) : guesses.some((r: any) => r.bestGuess.audioLanguage) ? guesses.filter((r: any) => r.bestGuess.audioLanguage).map((r: any) => (
                <div key={`ag-${r.paymentId}`} className="text-sm">
                  <span className="text-gray-500 text-xs">{r.tourName || r.productId}: </span>
                  <span className="font-medium">{langName(r.bestGuess.audioLanguage)}</span>{" "}
                  <Pill tone="amber">Best guess</Pill>
                </div>
              )) : <Pill tone="gray" title="Audio language was not recorded separately before 7 Sep 2026">Unknown</Pill>}
            </div>
          </div>

          {/* 4. E-mail language actually sent */}
          <div className="flex justify-between gap-6">
            <span className="text-gray-500 shrink-0">Tour-code e-mail sent in</span>
            <div className="text-right space-y-1">
              {linked.length > 0 ? linked.map((r: any) => (
                <div key={`e-${r.redemptionId}`} className="text-sm">
                  <span className="text-gray-500 text-xs">{r.tourName || r.productId}: </span>
                  <span className="font-medium">{langName(r.email?.renderedLanguage) || langName(r.language?.emailRendered) || "—"}</span>{" "}
                  <EmailStatusPill email={r.email} />
                  {r.email?.englishFallback && (
                    <span className="block text-[11px] text-amber-700">English fallback: {String(r.email.fallbackReason || "").replace(/_/g, " ")}</span>
                  )}
                </div>
              )) : guesses.length > 0 ? guesses.map((r: any) => (
                <div key={`eg-${r.paymentId}`} className="text-sm">
                  <span className="text-gray-500 text-xs">{r.tourName || r.productId}: </span>
                  <span className="font-medium capitalize">{langName(r.bestGuess.bookingLanguage) || "—"}</span>{" "}
                  <Pill tone="amber" title="The language the tour-code e-mail was written in, from the guessed booking. Whether the mail server accepted it was not recorded before 7 Sep 2026.">Best guess · outcome not recorded</Pill>
                </div>
              )) : (
                <>
                  <Pill tone="gray">Not recorded</Pill>
                  <span className="block text-xs text-gray-500 mt-1">The e-mail record book started on 7 Sep 2026.</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Entitlements & redemption history — linked rows first, legacy rows after */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-800 mb-1">Entitlements &amp; Redemption History</h2>
        <p className="text-xs text-gray-400 mb-4">
          <span className="font-medium text-green-700">Linked</span> rows show the exact booking this account used its code on.{" "}
          <span className="font-medium text-gray-600">Not linked (legacy)</span> rows predate that link; their booking, e-mail and language are not guessed.
        </p>
        {linked.length + legacy.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-500 uppercase border-b">
                <tr>
                  <th className="py-3 pr-4">Tour</th>
                  <th className="py-3 pr-4">City</th>
                  <th className="py-3 pr-4">Source</th>
                  <th className="py-3 pr-4">Booking ID</th>
                  <th className="py-3 pr-4">Marketplace ref</th>
                  <th className="py-3 pr-4">Booking e-mail</th>
                  <th className="py-3 pr-4">Language</th>
                  <th className="py-3 pr-4">Link</th>
                  <th className="py-3 pr-4">Redeemed</th>
                </tr>
              </thead>
              <tbody>
                {linked.map((r: any) => (
                  <tr key={`l-${r.redemptionId}`} className="border-b border-gray-50 hover:bg-gray-50 align-top">
                    <td className="py-3 pr-4 font-medium">
                      {r.tourName || r.productId}
                      {r.kind === "bundle" && <span className="ml-1 text-[11px] text-gray-500">(bundle)</span>}
                    </td>
                    <td className="py-3 pr-4">{r.cityName && <Pill tone="blue">{r.cityName}</Pill>}</td>
                    <td className="py-3 pr-4"><PlatformPill platform={r.platform} /></td>
                    <td className="py-3 pr-4 text-gray-500 font-mono text-xs">{r.bookingId || "—"}</td>
                    <td className="py-3 pr-4 text-gray-500 font-mono text-xs">{r.externalBookingRef || "—"}</td>
                    <td className="py-3 pr-4 text-gray-500 text-xs">
                      {r.bookingEmail || "—"}
                      {r.bookingEmail && user.email && r.bookingEmail.toLowerCase() !== String(user.email).toLowerCase() && (
                        <span className="block text-[11px] text-amber-700">differs from the app e-mail</span>
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      <span className="capitalize">{langName(r.language?.comm) || "Unknown"}</span>{" "}
                      <LanguageStatusPill status={r.language?.status} />
                    </td>
                    <td className="py-3 pr-4">
                      <Pill tone="green" title={r.identityAssurance === "authenticated" ? "Redeemed while signed in" : "Redeemed by an older app build (account id from the request body)"}>Linked</Pill>
                      <span className="block text-[11px] text-gray-500 mt-1">
                        {r.identityAssurance === "authenticated" ? "signed in" : "older app"}
                        {r.clientPlatform ? ` · ${r.clientPlatform}${r.appVersion ? ` ${r.appVersion}` : ""}` : ""}
                      </span>
                      {Array.isArray(r.participants) && r.participants.length > 0 && (
                        <span className="block text-[11px] text-gray-500 mt-1">
                          Also used by:{" "}
                          {r.participants.map((p: any, i: number) => (
                            <button
                              key={p.userId}
                              type="button"
                              onClick={() => navigate(`/users/${p.userId}`)}
                              className="text-aqua hover:underline"
                              title={p.email || ""}
                            >
                              {p.name || p.email || `user ${p.userId}`}{i < r.participants.length - 1 ? ", " : ""}
                            </button>
                          ))}
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-gray-500 whitespace-nowrap">{fmtDateTime(r.redeemedAt) || "—"}</td>
                  </tr>
                ))}
                {legacy.map((r: any) => {
                  const g = r.bestGuess || { match: "none" };
                  const hasGuess = g.match === "email_and_tour" || g.match === "only_booking_for_tour";
                  return (
                  <tr key={`g-${r.paymentId}`} className="border-b border-gray-50 hover:bg-gray-50 align-top text-gray-600">
                    <td className="py-3 pr-4 font-medium text-gray-700">
                      {r.tourName || r.productId}
                      {r.kind === "bundle" && <span className="ml-1 text-[11px] text-gray-500">(bundle)</span>}
                    </td>
                    <td className="py-3 pr-4">{r.cityName && <Pill tone="blue">{r.cityName}</Pill>}</td>
                    <td className="py-3 pr-4">
                      <Pill tone={r.origin === "iap" ? "purple" : r.origin === "manual_grant" ? "orange" : "gray"}>{ORIGIN_LABELS[r.origin] || r.origin}</Pill>
                      {(hasGuess ? g.platform : r.platform) && <span className="block text-[11px] text-gray-500 mt-1">{hasGuess ? g.platform : r.platform}</span>}
                    </td>
                    <td className={`py-3 pr-4 font-mono text-xs ${hasGuess ? "text-gray-500" : "text-gray-400"}`}>{hasGuess ? g.bookingId : "—"}</td>
                    <td className={`py-3 pr-4 font-mono text-xs ${hasGuess ? "text-gray-500" : "text-gray-400"}`}>{hasGuess ? (g.externalBookingRef || "—") : "—"}</td>
                    <td className={`py-3 pr-4 text-xs ${hasGuess ? "text-gray-500" : "text-gray-400"}`}>
                      {hasGuess ? (g.bookingEmail || "—") : "—"}
                      {hasGuess && g.match === "only_booking_for_tour" && g.bookingEmail && user.email && String(g.bookingEmail).toLowerCase() !== String(user.email).toLowerCase() && (
                        <span className="block text-[11px] text-amber-700">differs from the app e-mail</span>
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      {hasGuess ? (
                        <>
                          <span className="capitalize">{langName(g.commLanguage) || langName(g.bookingLanguage) || "Unknown"}</span>{" "}
                          <LanguageStatusPill status={g.languageStatus} />
                        </>
                      ) : <Pill tone="gray">Not verified</Pill>}
                    </td>
                    <td className="py-3 pr-4">
                      {hasGuess ? (
                        <>
                          <Pill tone="amber" title="Matched after the fact, not recorded at redemption time">Best guess</Pill>
                          <span className="block text-[11px] text-gray-500 mt-1">{guessLabel(g.match)}{g.candidateCount > 1 ? ` · ${g.candidateCount} bookings for this tour` : ""}</span>
                        </>
                      ) : (
                        <>
                          <Pill tone="gray" title="Granted before 7 Sep 2026; no booking is tied to this account">Not linked (legacy)</Pill>
                          <span className="block text-[11px] text-gray-500 mt-1">
                            {g.match === "not_applicable" ? "no booking expected" : g.candidateCount > 0 ? `${g.candidateCount} bookings for this tour, none with this e-mail` : "no booking found for this tour"}
                          </span>
                        </>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-gray-500 whitespace-nowrap">{hasGuess && g.redeemedAt ? fmtDateTime(g.redeemedAt) : fmtDate(r.dateOfOrder) || "—"}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-400">No entitlements yet</p>
        )}
      </div>

      {/* Purchased Tours */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800">Purchased Tours</h2>
          <button
            onClick={() => setShowAddTour(true)}
            className="px-4 py-2 bg-aqua text-white rounded-lg hover:opacity-90 transition"
          >
            + Add Tour
          </button>
        </div>
        {userTours.length > 0 ? (
          <ol className="list-decimal pl-6 space-y-1">
            {userTours.map((tour: any, idx: number) => (
              <li key={idx} className="text-gray-700">{tour.destinationName || tour.tourId}</li>
            ))}
          </ol>
        ) : (
          <p className="text-gray-400">No purchased tours</p>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <p className="text-lg font-semibold">Are you sure you want to delete this user?</p>
            <div className="mt-6 flex gap-4">
              <button onClick={handleDelete} className="px-6 py-3 bg-red-500 text-white rounded-lg">
                Yes, Delete
              </button>
              <button onClick={() => setShowDeleteConfirm(false)} className="px-6 py-3 border-2 rounded-lg">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Tour Modal — Searchable */}
      {showAddTour && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg w-[500px] max-h-[80vh] flex flex-col">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Add Tour</h3>
            <input
              type="text"
              placeholder="Search tours..."
              value={tourSearch}
              onChange={(e) => setTourSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 mb-3 outline-none focus:border-aqua text-sm"
              autoFocus
            />
            <div className="overflow-y-auto flex-1 max-h-[400px] border rounded-lg mb-4">
              {filteredTours
                .filter((t: any) => t.name?.toLowerCase().includes(tourSearch.toLowerCase()))
                .map((tour: any) => (
                  <div
                    key={tour.productId}
                    onClick={() => setSelectedTour(tour)}
                    className={`px-4 py-3 cursor-pointer border-b border-gray-50 text-sm transition ${
                      selectedTour?.productId === tour.productId
                        ? "bg-aqua bg-opacity-10 text-aqua font-semibold"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    {tour.name || tour.title || tour.productId}
                  </div>
                ))}
              {filteredTours.filter((t: any) => t.name?.toLowerCase().includes(tourSearch.toLowerCase())).length === 0 && (
                <div className="px-4 py-6 text-center text-gray-400 text-sm">No tours found</div>
              )}
            </div>
            {selectedTour && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                Selected: <strong>{selectedTour.name}</strong>
              </div>
            )}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => { setShowAddTour(false); setTourSearch(""); setSelectedTour(null); }}
                className="px-6 py-2 border border-aqua text-aqua rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!selectedTour}
                onClick={async () => {
                  if (!selectedTour) return;
                  const payload = {
                    ...formik.values,
                    phone: formik.values.contact,
                    cartDetails: [{
                      productId: selectedTour.productId,
                      tourId: selectedTour.id,
                      destinationName: selectedTour.name,
                      imagesUrl: selectedTour.imageUrl,
                      placeId: selectedTour.placeId,
                      status: 1,
                      amount: selectedTour.sellingPrice,
                      bundelsId: 1,
                    }],
                  };
                  try {
                    const res = await services.createUserApi(payload);
                    if (res.status === true) {
                      toast.success("Tour added");
                      setShowAddTour(false);
                      setTourSearch("");
                      setSelectedTour(null);
                      loadUserTours();
                    }
                  } catch {
                    toast.error("Error adding tour");
                  }
                }}
                className={`px-6 py-2 rounded-lg text-white ${selectedTour ? "bg-aqua hover:opacity-90" : "bg-gray-300 cursor-not-allowed"}`}
              >
                Add Tour
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
